/**
 * La continuité des mois d'un graphique.
 *
 * CE QUI SE PASSAIT. Les mois du graphique « Évolution des ventes » étaient
 * dérivés des seuls documents existants : un mois sans facture ni devis
 * n'apparaissait pas du tout. L'axe sautait de novembre 2025 à février 2026 en
 * les collant l'un à l'autre, sans rien qui signale les deux mois absents. Une
 * série temporelle qui saute des périodes se lit comme une série continue — et
 * ment sur la forme de l'activité : un trou d'activité y ressemble à une
 * activité régulière.
 *
 * Les mois vides sont donc rendus, à zéro. Ils existent, et ils se voient.
 */

const CLE_MOIS = /^\d{4}-\d{2}$/;

/** « 2025-12 » → « 2026-01 ». */
export function moisSuivant(cle: string): string {
  const [annee, mois] = cle.split('-').map(Number);
  return mois === 12
    ? `${annee + 1}-01`
    : `${annee}-${String(mois + 1).padStart(2, '0')}`;
}

/**
 * Toutes les clés de mois entre la plus ancienne et la plus récente, sans trou.
 *
 * Les clés qui ne sont pas des mois — « unknown », posée par les documents sans
 * date — ne participent pas à l'intervalle et sont rendues à la fin : les
 * écarter les ferait disparaître de l'écran.
 *
 * « maxMois » n'est pas un réglage d'affichage mais un garde-fou : une date
 * aberrante en base — une facture datée de l'an 1000 — engendrerait sinon des
 * dizaines de milliers de points. Au-delà, on rend les clés telles quelles
 * plutôt que d'inventer un axe.
 */
export function completerLesMois(cles: string[], maxMois = 240): string[] {
  const mois = cles.filter((c) => CLE_MOIS.test(c)).sort();
  const autres = cles.filter((c) => !CLE_MOIS.test(c)).sort();

  if (mois.length === 0) return autres;

  const premier = mois[0];
  const dernier = mois[mois.length - 1];

  const complet: string[] = [];
  let courant = premier;
  while (courant <= dernier && complet.length < maxMois) {
    complet.push(courant);
    courant = moisSuivant(courant);
  }

  // Intervalle aberrant : on ne fabrique pas un axe, on rend ce qu'on a.
  if (courant <= dernier) return [...mois, ...autres];

  return [...complet, ...autres];
}

/** La clé « YYYY-MM » d'une date. */
export function cleDuMois(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * L'ANNÉE GLISSANTE : les douze derniers mois, mois courant COMPRIS.
 *
 * En août 2026, cela va de septembre 2025 à août 2026. Ni onze mois ni treize,
 * et surtout aucun mois futur : la borne haute est la fin du mois courant, pas
 * une date plus lointaine. Un graphique qui affiche des mois à venir laisse
 * croire à une chute d'activité là où il n'y a que du temps qui n'a pas encore
 * eu lieu.
 */
export function anneeGlissante(maintenant: Date): { debut: Date; fin: Date } {
  const debut = new Date(maintenant.getFullYear(), maintenant.getMonth() - 11, 1, 0, 0, 0, 0);
  const fin = new Date(maintenant.getFullYear(), maintenant.getMonth() + 1, 0, 23, 59, 59, 999);
  return { debut, fin };
}

/**
 * Tous les mois d'une période, sans trou, bornes comprises.
 *
 * C'est cette liste qui fait l'axe du graphique — et non les documents
 * existants. Un mois sans la moindre facture doit s'afficher à zéro : c'est une
 * information, pas un vide à masquer. Une année glissante compte donc toujours
 * douze colonnes, même sur une société qui n'a facturé qu'une fois.
 */
export function moisDeLaPeriode(debut: Date, fin: Date, maxMois = 240): string[] {
  if (!(debut instanceof Date) || !(fin instanceof Date)) return [];
  if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime())) return [];

  const premier = cleDuMois(debut);
  const dernier = cleDuMois(fin);
  if (premier > dernier) return [];

  const mois: string[] = [];
  let courant = premier;
  while (courant <= dernier && mois.length < maxMois) {
    mois.push(courant);
    courant = moisSuivant(courant);
  }
  return mois;
}
